import React, { useCallback, useState, useEffect } from "react";
import { RefreshCw, Search, Sliders, X } from "react-feather";
import { Button, Card, FormGroup, Input, Label } from "reactstrap";
import { H5 } from "../../../AbstractElements";

const fieldStyle = {
  boxShadow: "none",
  border: "1.5px solid #a0aec0",
  borderRadius: "8px",
  fontSize: "13px",
  color: "#2d3748",
  backgroundColor: "#fafbfc",
  transition: "border-color 0.2s ease",
  height: "38px",
  width: "100%",
};

const labelStyle = {
  fontSize: "11px",
  fontWeight: "600",
  color: "#6b7a99",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: "5px",
  display: "block",
};

const FilterInput = ({ label, ...props }) => (
  <FormGroup className="mb-0">
    <Label style={labelStyle}>{label}</Label>
    <Input style={fieldStyle} {...props} />
  </FormGroup>
);

const FilterSelect = ({ label, options = [], ...props }) => (
  <FormGroup className="mb-0">
    <Label style={labelStyle}>{label}</Label>
    <Input
      type="select"
      style={{ ...fieldStyle, cursor: "pointer" }}
      {...props}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Input>
  </FormGroup>
);

const FilterDateRange = ({
  label,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
}) => (
  <FormGroup className="mb-0">
    <Label style={labelStyle}>{label}</Label>
    <div className="d-flex gap-1">
      <Input
        type="date"
        title="From"
        value={fromValue}
        onChange={onFromChange}
        style={{ ...fieldStyle, fontSize: "11px", minWidth: 0, flex: 1 }}
      />
      <Input
        type="date"
        title="To"
        value={toValue}
        onChange={onToChange}
        style={{ ...fieldStyle, fontSize: "11px", minWidth: 0, flex: 1 }}
      />
    </div>
  </FormGroup>
);

const renderField = (field, filters, handleFilterChange, handleKeyDown) => {
  switch (field.type) {
    case "text":
    case "email":
    case "tel":
      return (
        <FilterInput
          label={field.label}
          type={field.type}
          placeholder={field.placeholder || ""}
          value={filters[field.key] || ""}
          onChange={(e) => handleFilterChange(field.key, e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={field.maxLength}
        />
      );
    case "select":
      return (
        <FilterSelect
          label={field.label}
          options={field.options || []}
          value={filters[field.key] || ""}
          onChange={(e) => handleFilterChange(field.key, e.target.value)}
        />
      );
    case "daterange":
      return (
        <FilterDateRange
          label={field.label}
          fromValue={filters[`${field.key}From`] || ""}
          toValue={filters[`${field.key}To`] || ""}
          onFromChange={(e) =>
            handleFilterChange(`${field.key}From`, e.target.value)
          }
          onToChange={(e) =>
            handleFilterChange(`${field.key}To`, e.target.value)
          }
        />
      );
    default:
      return null;
  }
};

const FilterHeader = ({
  title,
  onReset,
  onSearch,
  onRefresh,
  isLoading,
  setData,
  data,
  originalData,
  fields = [],
  initialFilters,
}) => {
  const buildInitialFilters = useCallback(() => {
    const init = {};
    fields.forEach((field) => {
      if (field.type === "daterange") {
        init[`${field.key}From`] = initialFilters?.[`${field.key}From`] || "";
        init[`${field.key}To`] = initialFilters?.[`${field.key}To`] || "";
      } else {
        init[field.key] = initialFilters?.[field.key] || "";
      }
    });
    return init;
  }, [fields, initialFilters]);

  const [filters, setFilters] = useState(buildInitialFilters);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  useEffect(() => {
    if (!initialFilters) return;

    const hasValues = Object.values(initialFilters).some(
      (v) => v !== "" && v !== undefined && v !== null
    );

    setFilters(buildInitialFilters());
    setIsExpanded(hasValues ? true : isExpanded);
    setIsFiltered(hasValues);
  }, [initialFilters, buildInitialFilters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    if (onSearch) {
      const trimmedFilters = Object.keys(filters).reduce((acc, key) => {
        acc[key] =
          typeof filters[key] === "string" ? filters[key].trim() : filters[key];
        return acc;
      }, {});

      const hasActiveInputs = Object.values(trimmedFilters).some(
        (v) => v !== "" && v !== undefined
      );
      setIsFiltered(hasActiveInputs);

      onSearch(trimmedFilters);
    }
  };

  const handleReset = () => {
    const emptyFilters = {};
    fields.forEach((f) => {
      if (f.type === "daterange") {
        emptyFilters[`${f.key}From`] = "";
        emptyFilters[`${f.key}To`] = "";
      } else {
        emptyFilters[f.key] = "";
      }
    });

    setFilters(emptyFilters);
    setIsFiltered(false);

    if (onReset) {
      onReset();
    } else if (setData) {
      setData(originalData || data);
    }
  };

  const handleRefreshClick = (e) => {
    e.stopPropagation();
    if (onRefresh) onRefresh();
  };

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== "" && v !== undefined
  );

  const handleKeyDown = (e) => {
     if (e.key === "Enter" && hasActiveFilters) handleSearch();
  };

  const showControls = hasActiveFilters || isFiltered;

  return (
    <Card
      className="mb-4 w-100"
      style={{
        border: "1px solid #edf0f7",
        borderRadius: "14px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
    >
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          .spinning {
            animation: spin 1s linear infinite;
          }

          /* Placeholder colour — light grey across all browsers */
          .filter-header-body input::placeholder {
            color: #b0bcd4 !important;
            opacity: 1;
          }
          .filter-header-body input::-webkit-input-placeholder {
            color: #b0bcd4 !important;
            opacity: 1;
          }
          .filter-header-body input::-moz-placeholder {
            color: #b0bcd4 !important;
            opacity: 1;
          }
          .filter-header-body input:-ms-input-placeholder {
            color: #b0bcd4 !important;
            opacity: 1;
          }

          /* Subtle focus border glow on inputs & selects */
          .filter-header-body input:focus,
          .filter-header-body select:focus {
            border-color:  #a0aec0 !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12) !important;
            outline: none;
          }

          /* Refresh button tooltip */
          .refresh-btn {
            position: relative;
            cursor: pointer;
          }
          .refresh-tooltip {
            visibility: hidden;
            opacity: 0;
            background-color: #2d3748;
            color: #fff;
            font-size: 11px;
            font-weight: 600;
            text-align: center;
            padding: 4px 8px;
            border-radius: 6px;
            position: absolute;
            bottom: -30px;
            left: 50%;
            transform: translateX(-50%);
            white-space: nowrap;
            transition: opacity 0.2s ease;
            z-index: 10;
            pointer-events: none;
          }
          .refresh-btn:hover .refresh-tooltip {
            visibility: visible;
            opacity: 1;
          }
        `}
      </style>

      {/* ── Header row ─────────────────────────────────────────────── */}
      <div
        className="d-flex align-items-center justify-content-between px-3 px-md-4 py-3"
        style={{
          borderBottom: isExpanded ? "1px solid #edf0f7" : "none",
          backgroundColor: "#fff",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded((p) => !p)}
      >
        <div className="d-flex align-items-center gap-2">
          <Sliders size={16} color="#5a67d8" />
          <H5
            attrH5={{
              className: "m-0",
              style: { fontSize: "15px", fontWeight: "700", color: "#1a202c" },
            }}
          >
            {title}
          </H5>
          {isFiltered && (
            <span
              style={{
                backgroundColor: "#5a67d8",
                color: "#fff",
                fontSize: "10px",
                fontWeight: "700",
                borderRadius: "20px",
                padding: "1px 8px",
              }}
            >
              FILTERED
            </span>
          )}
        </div>

        <div className="d-flex align-items-center gap-3">
          <div
            className="refresh-btn"
            onClick={handleRefreshClick}
            style={{
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              backgroundColor: isLoading ? "#f0f2f5" : "transparent",
            }}
            onMouseEnter={(e) =>
              !isLoading &&
              (e.currentTarget.style.backgroundColor = "#f0f2f5")
            }
            onMouseLeave={(e) =>
              !isLoading &&
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <RefreshCw
              size={16}
              color={isLoading ? "#5a67d8" : "#718096"}
              className={isLoading ? "spinning" : ""}
            />
            <span className="refresh-tooltip">Refresh Data</span>
          </div>
          <span
            style={{ fontSize: "18px", color: "#a0aec0", userSelect: "none" }}
          >
            {isExpanded ? "−" : "+"}
          </span>
        </div>
      </div>

      {/* ── Filter body ─────────────────────────────────────────────── */}
      {isExpanded && (
        <div
          className="px-3 px-md-4 py-3 filter-header-body"
          style={{ backgroundColor: "#fafbfc" }}
        >
          <div className="row g-2 g-md-3">
            {fields.map((field) => (
              <div
                key={field.key}
                className={field.colClass || "col-6 col-md-4 col-lg-2"}
              >
                {renderField(field, filters, handleFilterChange, handleKeyDown)}
              </div>
            ))}
          </div>

          <div
            className="d-flex justify-content-end gap-2 mt-3 pt-3"
            style={{ borderTop: "1px solid #edf0f7" }}
          >
            {showControls && (
              <Button
                outline
                onClick={handleReset}
                style={{
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#718096",
                  backgroundColor: "#fff",
                  borderColor: "#e2e8f0",
                  padding: "8px 18px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#718096";
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.borderColor = "#718096";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#fff";
                  e.currentTarget.style.color = "#718096";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                <X size={14} /> Clear
              </Button>
            )}
            <button
              onClick={handleSearch}
              disabled={!showControls}
              style={{
                borderRadius: "8px",
                border: "none",
                fontSize: "13px",
                fontWeight: "700",
                padding: "8px 22px",
                backgroundColor: showControls ? "#667eea" : "#e2e8f0",
                color: showControls ? "#ffffff" : "#a0aec0",
                cursor: showControls ? "pointer" : "not-allowed",
                transition: "all 0.2s ease",
              }}
            >
              <Search size={14} /> Search
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default FilterHeader;